import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n, usePlanLimits, useTutorialIntro, useWorkspace, useCurrencyList, type PlanLimitsProp } from '@/composables';
import { $emitter } from '@/services/emitter';
import {
  getTrafficSourcesListApiRequest,
} from '@/services/api/traffic-sources-api';
import {
  updateTrafficSourcesStoreCacheApiRequest,
} from '@/services/api/api-services/traffic-sources';
import { useTableQuery } from '@/composables/useTableQuery';
import { useStore } from '@/store';

type CampaignPanelForm = Record<string, unknown> & {
  workspaceId?: string | null;
  name?: string;
  countryId?: number;
  trafficSource?: { id: string; name: string };
  tags?: unknown[];
  trackingDomainId?: string;
  uniquenessPeriod?: number;
  usePostback?: boolean;
  postbackPercent?: number | null;
  useTrafficLoss?: boolean;
  trafficLossPercent?: number | null;
  sendCustomConversionMode?: string;
  sendCustomConversionIds?: string[];
  doNotSendNotSpecifiedConversions?: boolean;
  userAgentClientHintsEnabled?: number;
  botFiltersMode?: number;
  currencyId?: string;
};

export type CampaignPanelOneProps = {
  form: CampaignPanelForm;
  defaultForm: CampaignPanelForm;
  errorsDict: Record<string, string[]>;
  edit: boolean;
  closeModal: () => void;
  allWorkspaceIds: string[];
  campaignInitialState: Campaign;
  defaultWorkspace: { id: string };
  workspacesList: unknown[];
  offerOptions: Offer[];
};

export const useCampaignPanelOne = (
  props: CampaignPanelOneProps,
  emit: {
    (event: 'change-tracking-domain', value: string): void;
    (event: 'change-workspace', value: string): void;
    (event: 'change-country', value: unknown): void;
  },
) => {
  const store = useStore();
  const workspaceApi = useWorkspace();
  const tableQueryApi = useTableQuery();
  const { t } = useI18n();
  const router = useRouter();
  const planLimitsControl = usePlanLimits();
  const { currencyListOrderedPreferred } = useCurrencyList();
  const trafficSourceSelectOptions = ref<TrafficSource[]>([]);

  const introStepsDefault = computed(() => ({
    2: `<div class='introjs-tooltip__title'>${t('Campaign Settings')}</div><div class='introjs-tooltip__content'>${t('Specify name of campaign')}</div>`,
    3: `<div class='introjs-tooltip__title'>${t('Campaign Settings')}</div><div class='introjs-tooltip__content'>${t('Select a traffic source')}</div>`,
  }));

  const tutorialIntro = useTutorialIntro(() => ({
    introStepsDefault: introStepsDefault.value,
    quickStartSection: 'campaigns',
  }));

  const hasPlanLimitsRestriction = (limitKey: PlanLimitsProp) =>
    planLimitsControl.hasPlanLimitsRestriction(limitKey);

  const FFconversionNotificationUrlEnabled = computed(
    () => store.user.featureFlags?.conversionNotificationUrl?.enabled,
  );

  const countryListPlusGlobal = computed(() => store.countryListPlusGlobal);

  const countryName = computed(() => {
    if (!props.form.countryId) return '';
    return (
      countryListPlusGlobal.value?.find((item) => item.id === props.form.countryId)?.name || ''
    );
  });

  const nameDescription = computed(
    () =>
      `${props.form.trafficSource?.name || t('None')} - ${countryName.value || t('Global')} - ${props.form.name || t('None')}`,
  );

  const trackingDomains = computed(() =>
    store.trackingDomainsWithAccountDefault.filter((item) => {
      if (!item.id) return true;
      return props.allWorkspaceIds.some((workspace) => item.workspaceIds.includes(workspace));
    }),
  );

  const trafficSourceSelectOptionsWithNone = computed(() => [
    {
      name: t('None'),
      id: '',
    },
    ...trafficSourceSelectOptions.value,
  ]);

  const uniquenessPeriod = computed({
    get: () => Number(props.form.uniquenessPeriod || 0) / 3600,
    set: (value: number) => {
      props.form.uniquenessPeriod = value * 3600;
    },
  });

  const useTrafficLoss = computed({
    get: () => Boolean(props.form.useTrafficLoss),
    set: (value: boolean) => {
      props.form.trafficLossPercent = value ? 0 : null;
      props.form.useTrafficLoss = value;
    },
  });

  const trafficLossPercent = computed({
    get: () => props.form.trafficLossPercent,
    set: (value: number | string) => {
      props.form.trafficLossPercent = Number((Number(value) || 0).toFixed(6).toString());
    },
  });

  const usePostback = computed({
    get: () => Boolean(props.form.usePostback),
    set: (value: boolean) => {
      props.form.postbackPercent = value ? 100 : null;
      props.form.usePostback = value;
    },
  });

  const postbackPercent = computed({
    get: () => props.form.postbackPercent,
    set: (value: number | string) => {
      props.form.postbackPercent = Number((Number(value) || 0).toFixed(6).toString());
    },
  });

  const customConversions = computed(() => {
    const customEvents = Object.values(tableQueryApi.tableCustomEvents.value) as customEvent[];
    return customEvents
      .filter(
        (item: customEvent) =>
          (item.sendPostback && item.status === 'active')
          || props.form.sendCustomConversionIds?.includes(item.id),
      )
      .map((item: customEvent) => ({
        ...item,
        name: item.status === 'archived' ? `${item.name} (${t('Archived')})` : item.name,
      }));
  });

  const showPlanLimitsWarning_uniqness = computed(
    () =>
      props.form.uniquenessPeriod !== props.defaultForm.uniquenessPeriod
      && hasPlanLimitsRestriction('uniquenessPeriod'),
  );

  const showPlanLimitsWarning_usePostback = computed(
    () => usePostback.value && hasPlanLimitsRestriction('sendPostbacksCampaign'),
  );

  const showPlanLimitsWarning_useTrafficLoss = computed(
    () => useTrafficLoss.value && hasPlanLimitsRestriction('trafficLossCampaign'),
  );

  const showPlanLimitsWarning_tags = computed(
    () => Boolean(props.form.tags?.length) && hasPlanLimitsRestriction('tagsCampaigns'),
  );

  const hasAtLeastOneConversion = computed(() => {
    const customEvents = Object.values(tableQueryApi.tableCustomEvents.value) as customEvent[];
    return Boolean(customEvents.filter((item) => item.status === 'active').length);
  });

  const fetchTrafficSourcesOptions = computed(() => {
    if (store.cache.trafficSources.isAllInCache) return undefined;
    return ({ search }: { search: string }) =>
      getTrafficSourcesListApiRequest({
        search,
        status: 'active',
        orderBy: 'updated',
        orderDirection: 'desc',
        limit: '200',
        columns: ['name', 'id', 'workspaceId'],
        workspaceId: props.form.workspaceId,
      });
  });

  const useClientHintsItems = computed(() => [
    {
      value: 0,
      text: t('Default (as in Global Settings)'),
    },
    {
      value: 1,
      text: t('Enabled (For This Campaign)'),
    },
    {
      value: 2,
      text: t('Disabled (For This Campaign)'),
    },
  ]);

  const botFiltersAccess = computed(() => store.billing.limits?.botFiltersAccess);

  const botFiltersModeItems = computed(() => [
    {
      value: 0,
      text: t('Default (as in Global Settings)'),
    },
    {
      value: 1,
      text: t('Enabled (For This Campaign)'),
      click:
        !botFiltersAccess.value
        && (() => {
          $emitter.emit('open-dialog', {
            name: 'ConfirmDialog',
            data: {
              onConfirm: () => {
                props.closeModal();
                router.push({
                  name: 'app.profile.bot-filters',
                });
              },
              title: '',
              message: t(
                'To enable bot filters for the campaign, you first need to activate this feature at the account level.<br>Click <b>“Go to Settings”</b> to proceed with purchase and activation of the filters.',
              ),
              confirmButtonText: t('Go to Settings'),
            },
          });
        }),
    },
    {
      value: 2,
      text: t('Disabled (For This Campaign)'),
    },
  ]);

  const loadTrafficSourceOptions = async () => {
    const { uploadedForWorkspace, isAllInCache, data, isUploaded } =
      store.cache.trafficSources;

    if (isUploaded && isAllInCache) {
      trafficSourceSelectOptions.value = data.filter(
        (item) =>
          workspaceApi.allPublicWorkspacesIds.value.includes(item.workspaceId)
          || item.workspaceId === props.form.workspaceId,
      );
      return;
    }

    if (isUploaded && !isAllInCache && uploadedForWorkspace === props.form.workspaceId) {
      trafficSourceSelectOptions.value = data;
      return;
    }

    if (props.form.workspaceId === props.defaultWorkspace.id) {
      trafficSourceSelectOptions.value = (await updateTrafficSourcesStoreCacheApiRequest({
        workspaceId: props.defaultWorkspace.id,
      })) as TrafficSource[];
      return;
    }

    trafficSourceSelectOptions.value = (await getTrafficSourcesListApiRequest({
      status: 'active',
      limit: '200',
      orderBy: 'updated',
      orderDirection: 'desc',
      columns: ['name', 'id', 'workspaceId'],
      workspaceId: props.form.workspaceId,
    })) as TrafficSource[];
  };

  const onChangeWorkspaceUpdateTrafficSource = async (workspaceId: string) => {
    const { uploadedForWorkspace, isAllInCache, data, isUploaded } =
      store.cache.trafficSources;

    if (isUploaded && isAllInCache) {
      trafficSourceSelectOptions.value = data.filter(
        (item) =>
          workspaceApi.allPublicWorkspacesIds.value.includes(item.workspaceId)
          || item.workspaceId === props.form.workspaceId,
      );
      return;
    }

    if (isUploaded && !isAllInCache && uploadedForWorkspace === workspaceId) {
      trafficSourceSelectOptions.value = [...data];
      return;
    }

    trafficSourceSelectOptions.value = (await getTrafficSourcesListApiRequest({
      status: 'active',
      orderBy: 'updated',
      orderDirection: 'desc',
      limit: '200',
      columns: ['name', 'id', 'workspaceId'],
      workspaceId,
    })) as TrafficSource[];
  };

  const onChangeTrackingDomain = (value: string) => {
    emit('change-tracking-domain', value);
  };

  const onChangeWorkspace = (value: string) => {
    props.form.trafficSource = {
      name: t('None'),
      id: '',
    };
    onChangeWorkspaceUpdateTrafficSource(value);
    emit('change-workspace', value);
  };

  const onChangeCountry = (value: unknown) => {
    emit('change-country', value);
  };

  const clearTags = () => {
    props.form.tags = [];
  };

  const onSendCustomConversionsClick = (value: string) => {
    if ((value === 'include' || value === 'exclude') && !hasAtLeastOneConversion.value) {
      $emitter.emit('open-dialog', {
        name: 'CustomConversionsSending',
      });
      return true;
    }
    return undefined;
  };

  const resetPostback = () => {
    props.form.usePostback = false;
    props.form.postbackPercent = null;
  };

  const resetTrafficLoss = () => {
    props.form.useTrafficLoss = false;
    props.form.trafficLossPercent = null;
  };

  const resetUniquenessPeriod = () => {
    props.form.uniquenessPeriod = 86400;
  };

  onMounted(() => {
    loadTrafficSourceOptions();
  });

  return {
    store,

    t,
    getIntroParams: tutorialIntro.getIntroParams,
    trafficSourceSelectOptionsWithNone,
    fetchTrafficSourcesOptions,
    countryListPlusGlobal,
    nameDescription,
    trackingDomains,
    uniquenessPeriod,
    usePostback,
    postbackPercent,
    useTrafficLoss,
    trafficLossPercent,
    customConversions,
    showPlanLimitsWarning_uniqness,
    showPlanLimitsWarning_usePostback,
    showPlanLimitsWarning_useTrafficLoss,
    showPlanLimitsWarning_tags,
    FFconversionNotificationUrlEnabled,
    useClientHintsItems,
    botFiltersModeItems,
    currencyListOrderedPreferred,
    offerOptions: computed(() => props.offerOptions),
    onChangeTrackingDomain,
    onChangeWorkspace,
    onChangeCountry,
    clearTags,
    onSendCustomConversionsClick,
    resetPostback,
    resetTrafficLoss,
    resetUniquenessPeriod,
  };
};
